import React, { useState, useEffect, useRef } from 'react';
import { mapsService } from '../../api/services/maps';
import './PlacesAutocomplete.scss';

export const PlacesAutocomplete = ({
  value,
  onChange,
  placeholder = 'Enter location',
  className = '',
  disabled = false,
  onPlaceSelect = null,
  ...inputProps
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search function
  const searchPlaces = async (input) => {
    if (!input || input.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await mapsService.getAutocompleteSuggestions(input, {
        includedPrimaryTypes: [
          'locality',
          'administrative_area_level_1',
          'country',
        ],
        locationBias: {
          circle: {
            center: { latitude: 37.7749, longitude: -122.4194 }, // Default to SF
            radius: 50000,
          },
        },
      });

      if (result.success) {
        setSuggestions(result.data.suggestions || []);
        setShowDropdown(result.data.suggestions.length > 0);
        setSelectedIndex(-1);
      } else {
        setError(result.error);
        setSuggestions([]);
        setShowDropdown(false);
      }
    } catch (err) {
      console.error('[PlacesAutocomplete] Search error:', err);
      setError(err.message);
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce search
    debounceRef.current = setTimeout(() => {
      searchPlaces(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    const displayText =
      suggestion.placePrediction?.text?.text || suggestion.text || '';
    onChange(displayText);
    setSuggestions([]);
    setShowDropdown(false);
    setSelectedIndex(-1);

    if (onPlaceSelect) {
      onPlaceSelect({
        text: displayText,
        placeId: suggestion.placePrediction?.placeId,
        types: suggestion.placePrediction?.types || [],
        raw: suggestion,
      });
    }

    inputRef.current?.blur();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className={`places-autocomplete-container ${className}`}>
      <div className='places-autocomplete-input-wrapper'>
        <input
          ref={inputRef}
          type='text'
          value={value || ''}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className='places-autocomplete-input'
          autoComplete='off'
          {...inputProps}
        />

        {isLoading && (
          <div className='places-autocomplete-loading'>
            <div className='loading-spinner' />
          </div>
        )}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div ref={dropdownRef} className='places-autocomplete-dropdown'>
          {suggestions.map((suggestion, index) => {
            const text =
              suggestion.placePrediction?.text?.text || suggestion.text || '';
            const secondaryText =
              suggestion.placePrediction?.structuredFormat?.secondaryText
                ?.text || '';

            return (
              <div
                key={suggestion.placePrediction?.placeId || index}
                className={`places-autocomplete-item ${
                  index === selectedIndex ? 'selected' : ''
                }`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className='suggestion-icon'>📍</div>
                <div className='suggestion-content'>
                  <div className='suggestion-main'>{text}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {error && <div className='places-autocomplete-error'>{error}</div>}
    </div>
  );
};
